import { chain } from 'obliterator';
import { AlwaysDepth, BufferAttribute, LessEqualDepth } from 'three';
import type { BufferGeometry, Color, Float32BufferAttribute } from 'three';

import {
  getOrCreateThreeColor,
  defaultColor,
  defaultColorArray,
  getOrCreateThreeColorArray,
  type ColorRGBArray,
  type ColorRGBAArray,
} from '../colors';
import {
  COLOR_CODES,
  ENTITY_TYPES,
  MATERIAL_PROPERTIES,
  OPACITY_MAP,
  VECTORIZATION_EXPORT_LAYERS,
} from '../constants';
import { PART_TYPES } from '../constants';
import { AssemblyInstance } from '../loaders/assemblyInstance';
import type { AnyBody, Body } from '../loaders/body';
import { STEPBody, defaultEdgeColorComponents } from '../loaders/step/STEPBody';
import { StepEdgeModel } from '../loaders/step/StepEdgeModel';
import { StepFaceModel } from '../loaders/step/StepFaceModel';
import { StepTextModel } from '../loaders/step/StepTextModel';
import { StepVertexBody } from '../loaders/step/StepVertexBody';
import { fillRepeatedPattern } from '../loaders/step/utils';
import { constructEntityName } from '../loaders/step/utils';
import { STLBody } from '../loaders/stl/STLBody';
import { maxSTLVertices } from '../loaders/stl/STLGeometry';
import { areObjectsDifferentForKeys, areArraysAsSetDifferent } from '../utils';
import { syncFlatPartLineSegments2Colors } from './flat-part-line-mesh';
import { getAllAffectedBodyIds } from './focus-utils';
import type { RendererState } from './renderer';

const partColor = getOrCreateThreeColorArray(COLOR_CODES.PART_COLOR);
const assemblySelectedColor = getOrCreateThreeColorArray(
  COLOR_CODES.assemblySelected,
);

const paintExplicitStlFaces = (
  color: ColorRGBArray,
  geometries: BufferGeometry[], //buffer geometries for the STL
): void => {
  for (const geometry of geometries) {
    const positionCount = (
      geometry.attributes.position as Float32BufferAttribute
    ).array.length;
    const vertexCount = positionCount / 3; //Same as geometry.attributes.position.count (or 3 times the number of faces)
    const existingColors = geometry.attributes.color as Float32BufferAttribute;
    const colors: Float32Array = existingColors
      ? (existingColors.array as Float32Array) //existing will be Float32Array because of geometry.setAttribute('color', ...) below
      : new Float32Array(positionCount);
    //set color for each vertex
    fillRepeatedPattern(color, colors, 0, vertexCount);
    if (existingColors) {
      if (!existingColors.needsUpdate) {
        existingColors.needsUpdate = true;
        existingColors.version++; //must update when needsUpdate is set to true
      }
    } else {
      geometry.setAttribute('color', new BufferAttribute(colors, 3));
    }
  }
};

export const handleUpdateAdditionalFeedback = (
  thisState: RendererState,
  nextState: RendererState,
): boolean => {
  const shouldUpdate =
    areObjectsDifferentForKeys(thisState, nextState, [
      'showAdditionalKeys',
      'showAdditionalKeysExplicit',
    ]) || !!nextState.forceUpdateAdditionalFeedback;

  if (shouldUpdate) {
    const { bodyOrAssemblyInstanceById } = nextState;
    for (const bodyOrAssembly of bodyOrAssemblyInstanceById.values()) {
      // first turn all visibility off
      for (const key in bodyOrAssembly.additionalFeedback) {
        bodyOrAssembly.additionalFeedback[key].visible = false;
      }
      // then turn on visibility of affected keys
      for (const key of chain(
        nextState.showAdditionalKeys,
        nextState.showAdditionalKeysExplicit,
      )) {
        if (key in bodyOrAssembly.additionalFeedback) {
          bodyOrAssembly.additionalFeedback[key].visible = true;
        }
      }
    }
  }
  return shouldUpdate;
};

//SelectedBodiesPreviousColorState value is R, G, B, and alpha values for each BufferAttribute
type SelectedBodiesPreviousColorState = Map<AnyBody, Float32Array[]>;
let lastPaintedSelectedBodies: SelectedBodiesPreviousColorState = new Map();

const paintSelectedBodies = ({
  ids,
  bodyOrAssemblyInstanceById,
  color,
  alwaysDepth,
}: {
  ids: string[];
  bodyOrAssemblyInstanceById: RendererState['bodyOrAssemblyInstanceById'];
  color: ColorRGBArray;
  alwaysDepth?: boolean;
}): void => {
  // const start = performance.now();
  const crumbs: SelectedBodiesPreviousColorState = new Map(); //recorded of painted body ids
  if (ids.length) {
    for (const bodyOrAssemblyInstanceId of ids) {
      const bodyOrAssemblyInstance = bodyOrAssemblyInstanceById.get(
        bodyOrAssemblyInstanceId,
      );
      if (bodyOrAssemblyInstance) {
        for (const childBody of bodyOrAssemblyInstance instanceof
        AssemblyInstance
          ? bodyOrAssemblyInstance.descendentBodies()
          : [bodyOrAssemblyInstance]) {
          if (childBody.isLoaded && !crumbs.has(childBody)) {
            const lastPaintedSelectedBody =
              lastPaintedSelectedBodies.get(childBody);
            if (lastPaintedSelectedBody) {
              //already painted as selected
              crumbs.set(childBody, lastPaintedSelectedBody);
              lastPaintedSelectedBodies.delete(childBody);
            } else {
              //Must be painted as selected
              if (childBody.obj instanceof STLBody) {
                crumbs.set(
                  childBody,
                  childBody.obj.geometries.map(
                    (g) => g.attributes.color.array.slice() as Float32Array,
                  ),
                );
                paintExplicitStlFaces(color, childBody.obj.geometries);
                childBody.obj.material.opacity = 1;
              } else {
                const faceColors = childBody.obj.faceGeometries.map(
                  (g) => g.attributes.color.array.slice() as Float32Array,
                );
                const edgeColors = childBody.obj.edgeGeometries.map(
                  (g) => g.attributes.color.array.slice() as Float32Array,
                );
                crumbs.set(childBody, faceColors.concat(edgeColors));
                childBody.obj.faceMaterial.depthFunc = alwaysDepth
                  ? AlwaysDepth
                  : LessEqualDepth;
                const cAndO: ColorRGBAArray = [...color, 1.0];
                childBody.obj.faceGeometries.forEach((g) => {
                  const existingColors = g.attributes
                    .color as Float32BufferAttribute;
                  fillRepeatedPattern(
                    cAndO,
                    existingColors.array as Float32Array,
                  );
                  if (!existingColors.needsUpdate) {
                    existingColors.needsUpdate = true;
                    existingColors.version++; //must update when needsUpdate is set to true
                  }
                });
                childBody.obj.edgeGeometries.forEach((g) => {
                  const existingColors = g.attributes
                    .color as Float32BufferAttribute;
                  fillRepeatedPattern(
                    cAndO,
                    existingColors.array as Float32Array,
                  );
                  if (!existingColors.needsUpdate) {
                    existingColors.needsUpdate = true;
                    existingColors.version++; //must update when needsUpdate is set to true
                  }
                });
              }
            }
          }
        }
      }
    }
  }
  //Restore colors of no longer selectedBodies
  for (const [
    childBody,
    previousColors,
  ] of lastPaintedSelectedBodies.entries()) {
    if (childBody.obj instanceof STLBody) {
      for (const [i, previousColor] of previousColors.entries()) {
        childBody.obj.geometries[i].setAttribute(
          'color',
          new BufferAttribute(previousColor, 3),
        );
      }
    } else {
      childBody.obj.faceMaterial.depthFunc = alwaysDepth
        ? AlwaysDepth
        : LessEqualDepth;
      const faceCount = childBody.obj.faceGeometries.length;
      const edgeCount = childBody.obj.edgeGeometries.length;
      childBody.obj.faceGeometries.forEach((g, i) => {
        const existingColors = g.attributes.color as Float32BufferAttribute;
        existingColors.array.set(previousColors[i]);
        if (!existingColors.needsUpdate) {
          existingColors.needsUpdate = true;
          existingColors.version++; //must update when needsUpdate is set to true
        }
      });
      if (previousColors.length >= faceCount + edgeCount) {
        childBody.obj.edgeGeometries.forEach((g, i) => {
          const existingColors = g.attributes.color as Float32BufferAttribute;
          existingColors.array.set(previousColors[faceCount + i]);
          if (!existingColors.needsUpdate) {
            existingColors.needsUpdate = true;
            existingColors.version++; //must update when needsUpdate is set to true
          }
        });
      }
    }
  }

  //Reset lastPaintedSelectedBodies
  lastPaintedSelectedBodies = crumbs;
  // console.log(`paintSelectedBodies(): ${performance.now() - start}`);
};

type StlData = [BufferGeometry[], number[], ColorRGBArray]; //STL's bufferGeometries, indices, color

let previousHighlightedSTLIndices: Map<
  BufferGeometry[],
  Set<number>
> = new Map();
//paintHighlightedSTLFaces:
//* Always resets geometry/indicies in previousHighlightedSTLIndices that are not in the next geometryHighlights
//* If there are no geometryHighlights, it will reset colors of geometry/indices in previousHighlightedSTLIndices
const paintHighlightedSTLFaces = (
  geometryHighlights: StlData[],
  originalColor: ColorRGBArray,
) => {
  const newHighlightedSTLIndices: Map<
    BufferGeometry[],
    Set<number>
  > = new Map();
  for (const [geometries, faceIndices, color] of geometryHighlights) {
    const previousIndicies = previousHighlightedSTLIndices.get(geometries);

    //Color just faces in faceIndices
    //* By convention faces are looked up by their vertex a's index in BufferGeometry positions
    for (const i of faceIndices) {
      //repeat r, g, b values for each face vertex
      previousIndicies?.delete(i);
      const i0 = (i % maxSTLVertices) * 3;
      const bufferNum = Math.trunc(i / maxSTLVertices);
      const geometry = geometries[bufferNum];
      const colorAttribute = geometry.attributes
        .color as Float32BufferAttribute;
      if (!colorAttribute.needsUpdate) {
        colorAttribute.needsUpdate = true;
        colorAttribute.version++; //must update when needsUpdate is set to true
      }
      const colors = colorAttribute.array as Float32Array;
      colors.set(color, i0);
      colors.set(color, i0 + 3);
      colors.set(color, i0 + 6);
    }
    if (faceIndices.length) {
      let fi = newHighlightedSTLIndices.get(geometries);
      if (fi) {
        //only defined if same geometries is in multiple entries in geometryHighlights
        for (const i of faceIndices) {
          fi.add(i);
        }
      } else {
        fi = new Set(faceIndices);
      }
      newHighlightedSTLIndices.set(geometries, fi);
    }
  }
  for (const [
    geometries,
    previousIndicies,
  ] of previousHighlightedSTLIndices.entries()) {
    //Reset color of previously highlighted faces
    for (const i of previousIndicies) {
      const i0 = (i % maxSTLVertices) * 3;
      const bufferNum = Math.trunc(i / maxSTLVertices);

      const geometry = geometries[bufferNum];
      const colorAttribute = geometry.attributes
        .color as Float32BufferAttribute;
      if (!colorAttribute.needsUpdate) {
        colorAttribute.needsUpdate = true;
        colorAttribute.version++; //must update when needsUpdate is set to true
      }
      const colors = colorAttribute.array as Float32Array;
      colors.set(originalColor, i0);
      colors.set(originalColor, i0 + 3);
      colors.set(originalColor, i0 + 6);
    }
  }
  previousHighlightedSTLIndices = newHighlightedSTLIndices;
};

type HighlightData = ColorRGBAArray; //color, opacity

type HighlightedFaces = Map<StepFaceModel, HighlightData>;
let previousHighlightedFaces: HighlightedFaces = new Map(); //Map's value is the original color and opacity
//paintHighlightedFaces:
//* Always resets highlights in previousHighlightedFaces that are not in the next highlighted faces
//* If there are no faces, it will reset all previous highlights
const paintHighlightedFaces = (
  faces: HighlightedFaces, //Map's value is the new color and opacity
) => {
  const newHighlighedFaces: HighlightedFaces = new Map(); //Map's value is the original color and opacity
  //highlight the faces
  for (const [face, colorAndOpacity] of faces) {
    newHighlighedFaces.set(
      face,
      previousHighlightedFaces.get(face) ?? face.colorAndOpacity,
    ); //capture original color and opacity
    previousHighlightedFaces.delete(face); //check it off as painted
    face.setColorAndOpacity(colorAndOpacity);
  }
  //reset color of previously selected faces
  for (const [face, colorAndOpacity] of previousHighlightedFaces) {
    face.setColorAndOpacity(colorAndOpacity);
  }
  previousHighlightedFaces = newHighlighedFaces;
};

type HighlightedEdges = Map<StepEdgeModel, HighlightData>;
let previousHighlightedEdges: HighlightedEdges = new Map(); //Map's value is the original color and opacity
//paintHighlightedEdges:
//* Always resets highlights in paintHighlightedEdges that are not in the next highlighted edges
//* If there are no edges, it will reset all previous highlights
const paintHighlightedEdges = (
  edges: HighlightedEdges, //Map's value is the new color and opacity
) => {
  const newHighlighedEdges: HighlightedEdges = new Map(); //value is the original color and opacity
  //highlight the edges
  for (const [edge, colorAndOpacity] of edges) {
    newHighlighedEdges.set(
      edge,
      previousHighlightedEdges.get(edge) ?? edge.colorAndOpacity,
    ); //capture original color and opacity
    previousHighlightedEdges.delete(edge); //check it off as painted
    edge.setColorAndOpacity(colorAndOpacity);
  }
  //reset color of previously selected edges
  for (const [edge, colorAndOpacity] of previousHighlightedEdges) {
    edge.setColorAndOpacity(colorAndOpacity);
  }
  previousHighlightedEdges = newHighlighedEdges;
};

type HighlightedVertices = Map<StepVertexBody, [Color, number]>;
type HighlightedVerticesSet = Set<StepVertexBody>;
let previousHighlightedVertices: HighlightedVerticesSet = new Set(); //Map's value is the original color
//paintHighlightedVertices:
//* Always resets highlights in previousHighlightedVertices
//* If there are no vertices, it will reset all previous highlights
const paintHighlightedVertices = (
  vertices: HighlightedVertices, //Map's value is the new color and opacity
) => {
  const newHighlighedVertices: HighlightedVerticesSet = new Set();
  //highlight the vertices
  for (const [vertex, [color, opacity]] of vertices) {
    newHighlighedVertices.add(vertex);
    previousHighlightedVertices.delete(vertex); //check it off as painted
    vertex.renderable.visible = true;
    vertex.material.color.copy(color);
    vertex.material.opacity = opacity;
  }
  //turn off previous highlighted vertices
  for (const vertex of previousHighlightedVertices) {
    vertex.renderable.visible = false;
  }
  previousHighlightedVertices = newHighlighedVertices;
};

type HighlightedTexts = Map<StepTextModel, [Color, number]>;
let previousHighlightedTexts: HighlightedTexts = new Map(); //Map's value is the original color and opacity
//paintHighlightedTexts:
//* Always resets highlights in previousHighlightedTexts that are not in the next highlighted texts
//* If there are no texts, it will reset all previous highlights
const paintHighlightedTexts = (
  texts: HighlightedTexts, //Map's value is the new color and opacity
) => {
  const newHighlighedTexts: HighlightedTexts = new Map(); //value is the original color and opacity
  //highlight the texts
  for (const [text, [color, opacity]] of texts) {
    newHighlighedTexts.set(
      text,
      previousHighlightedTexts.get(text) ?? [text.color, text.opacity],
    ); //capture original color and opacity
    previousHighlightedTexts.delete(text); //check it off as painted
    text.setColor(color);
    text.opacity = opacity;
  }
  //reset color of previously selected texts
  for (const [text, [color, opacity]] of previousHighlightedTexts) {
    text.setColor(color);
    text.opacity = opacity;
  }
  previousHighlightedTexts = newHighlighedTexts;
};

const paintAndUpdateHighlights = (
  highlight: RendererState['highlight'],
  internalHighlight: RendererState['internalHighlight'],
  wireframe: RendererState['wireframe'],
  bodyOrAssemblyInstanceById: RendererState['bodyOrAssemblyInstanceById'],
  isBodyVisible: (body: AnyBody) => boolean,
) => {
  const stlHighlights: StlData[] = [];
  const faceHighlights: HighlightedFaces = new Map();
  const edgeHighlights: HighlightedEdges = new Map();
  const vertexHighlights: HighlightedVertices = new Map();
  const textHighlights: HighlightedTexts = new Map();
  for (const {
    body,
    indices,
    type,
    color,
    opacity,
    visibility,
    //linewidth, //not supported today
  } of chain(highlight || [], internalHighlight || [])) {
    if (!wireframe || (wireframe && type !== ENTITY_TYPES.FACE)) {
      const b = bodyOrAssemblyInstanceById.get(body) as AnyBody;
      const c = getOrCreateThreeColorArray(color);
      if (b.isLoaded && isBodyVisible(b)) {
        if (b.obj instanceof STLBody) {
          stlHighlights.push([b.obj.geometries, indices, c]);
        } else {
          //Recall edges and faces are packed.  When the body is visible, but a subset of the edges or faces or not,
          //then use opacity to control visibility. This is necessary for Vector Editor.
          const o =
            visibility === false ? 0.0 : opacity == null ? 1.0 : opacity;
          const cAndO: ColorRGBAArray = [...c, o];
          const { faces, edges, vertices, texts } = b.obj;
          let vc: Color | undefined;
          switch (type) {
            case ENTITY_TYPES.FACE:
              for (const index of indices) {
                const f = faces.get(
                  constructEntityName(b.id ?? 'undefined', type, index),
                );
                if (f) {
                  faceHighlights.set(f, cAndO);
                }
              }
              break;
            case ENTITY_TYPES.EDGE:
              for (const index of indices) {
                const e = edges.get(
                  constructEntityName(b.id ?? 'undefined', type, index),
                );
                if (e) {
                  edgeHighlights.set(e, cAndO);
                }
              }
              break;
            case ENTITY_TYPES.VERTEX:
              vc = getOrCreateThreeColor(color);
              for (const index of indices) {
                const v = vertices.get(
                  constructEntityName(b.id ?? 'undefined', type, index),
                );
                if (v) {
                  vertexHighlights.set(v, [vc, o]);
                }
              }
              break;
            case ENTITY_TYPES.TEXT:
              vc = getOrCreateThreeColor(color);
              for (const index of indices) {
                const t = texts.get(
                  constructEntityName(b.id ?? 'undefined', type, index),
                );
                if (t) {
                  textHighlights.set(t, [vc, o]);
                }
              }
              break;
          }
        }
      }
    }
  }

  //Paint the highlighted faces (and reset the no longer highlighted objects)
  paintHighlightedSTLFaces(stlHighlights, defaultColorArray);
  paintHighlightedFaces(faceHighlights);
  paintHighlightedEdges(edgeHighlights);
  paintHighlightedVertices(vertexHighlights);
  paintHighlightedTexts(textHighlights);
  // console.log(`highlights done: ${performance.now() - start}`);
};

const clearCaches = () => {
  //Note: caches are used to track the last selected bodies (that were painted) and the last highlighted objects (that were painted).
  //When painting the next set of selectedBodies and highlighted objects, some or all of the previously painted objects may need to
  //be reset to their previous color.  When painting the next selections and next highlights, the objects are removed from the caches for
  //the previous paints so that the remaining items in the cache are the ones that must be repainted back to their original colors.
  //But when paintAll() occurs, we are re-painting everything from scratch, so the caches no longer apply and should be cleared.

  lastPaintedSelectedBodies.clear();

  //highlight caches
  previousHighlightedSTLIndices.clear();
  previousHighlightedFaces.clear();
  previousHighlightedEdges.clear();
  previousHighlightedVertices.clear();
  previousHighlightedTexts.clear();
};

const createIsVisible = (
  visibleStockResultKeys: RendererState['visibleStockResultKeys'],
  bodyOrAssemblyInstanceById: RendererState['bodyOrAssemblyInstanceById'],
  visibleBodies: RendererState['visibleBodies'],
): [(body: AnyBody) => boolean, Body<PART_TYPES.STEP>[]] => {
  const stockBodies: AnyBody[] = visibleStockResultKeys.flatMap((key) => {
    const assemblyInstance = bodyOrAssemblyInstanceById.get(key) as
      | AssemblyInstance
      | undefined;
    const _bodies = assemblyInstance
      ? Array.from(assemblyInstance.descendentBodies())
      : [];
    return _bodies;
  });

  //Note: visibleBodies sometimes only contains assemblyInstances and not their children. (This happens when using online metals sheetmetal nesting)
  //- Often a body's parent assemblyInstances are not included (and that's okay).
  //- And usually when an assemblyInstance is visible, its descendent bodies are too. But don't trust all bodies will be included. (They aren't with sheetmetal nesting in online metals app)
  //- So using getAllAffectedBodyIds to ensure all visibleBodyIds are considered!
  const visibleBodyIds = getAllAffectedBodyIds(
    bodyOrAssemblyInstanceById,
    visibleBodies,
  );
  for (const b of stockBodies) {
    visibleBodyIds.add(b.id as string);
  }

  //Returns [isBodyVisible and stockBodies]
  //isBodyVisible stops the momentary flashing of non-visible bodies when first loading
  return [
    (body: AnyBody): boolean => visibleBodyIds.has(body.id as string),
    stockBodies,
  ];
};

//paintAll() is intended to paint everything (First pass or to reset colors)
let isFirstPaint = true;
const paintAll = ({
  bodies,
  bodyOrAssemblyInstanceById,
  focusedBodies,
  isFocusedViewActive,
  onlyShowHighlighted,
  renderNativeColors,
  visibleBodies,
  visibleStockResultKeys,
  wireframe,
  force,
}: {
  bodies: AnyBody[];
  bodyOrAssemblyInstanceById: Map<string, AnyBody | AssemblyInstance>;
  focusedBodies: string[];
  isFocusedViewActive?: boolean;
  onlyShowHighlighted?: boolean;
  renderNativeColors?: boolean;
  visibleBodies: string[];
  visibleStockResultKeys: string[];
  wireframe?: boolean;
  force?: boolean;
}): ReturnType<typeof createIsVisible> => {
  // const start = performance.now();
  const allFocusedBodies = isFocusedViewActive
    ? getAllAffectedBodyIds(
        bodyOrAssemblyInstanceById,
        chain(visibleBodies, focusedBodies),
      )
    : undefined;

  const [isBodyVisible, stockBodies] = createIsVisible(
    visibleStockResultKeys,
    bodyOrAssemblyInstanceById,
    visibleBodies,
  );

  if (isFirstPaint || force) {
    isFirstPaint = false;

    for (const body of bodies) {
      if (!body.isLoaded) {
        continue;
      }

      const visibility = isBodyVisible(body);
      body.group.visible = visibility;

      if (body.obj instanceof STLBody) {
        body.renderable.visible = visibility;
        const wf = !!wireframe;
        //body.obj.fullDetailRep.visible
        const facesVisible = visibility && !wf; //visibility of mesh
        if (facesVisible) {
          for (const mesh of body.obj.meshes) {
            mesh.visible = facesVisible;
          }
          paintExplicitStlFaces(
            renderNativeColors ? defaultColorArray : body.obj.baseRGBColor,
            body.obj.geometries,
          );
          body.obj.material.opacity = body.obj.baseOpacity;
        }
        for (const wireframe of body.obj.wireframes) {
          wireframe.visible = wf;
        }
      } else {
        //STEP/DXF:

        body.obj.edgesGroup.visible = body.obj.textsGroup.visible = visibility;
        const facesVisible = (body.obj.facesGroup.visible =
          visibility && !wireframe);

        if (facesVisible) {
          //Paint faces
          body.obj.faceMaterial.depthFunc = onlyShowHighlighted
            ? AlwaysDepth
            : LessEqualDepth;
          const isFocused =
            !allFocusedBodies || allFocusedBodies.has(body.id as string);
          for (const face of body.obj.faces.values()) {
            const { layer, visible } = face.style;
            const c = renderNativeColors
              ? defaultColorArray
              : layer
              ? //If the layer is a known vectorization layer, use the layer's color. Otherwise fallback to entity's style.color or host object's default color
                VECTORIZATION_EXPORT_LAYERS.find(({ key }) => key === layer)
                  ?.color ?? face.defaultColor
              : // if no layer, then use entity's metaData.color or host's object's default color
              face.metaData.color != null
              ? getOrCreateThreeColorArray(face.metaData.color)
              : face.defaultColor;
            const o =
              visible === false || visible === 0
                ? 0
                : isFocused
                ? renderNativeColors
                  ? MATERIAL_PROPERTIES.OPACITY
                  : face.defaultOpacity
                : OPACITY_MAP.UNFOCUSED;
            face.setColorAndOpacity([...c, o], false);
          }
        }

        if (visibility) {
          //Paint edges
          body.obj.edgeMaterial.depthFunc = onlyShowHighlighted
            ? AlwaysDepth
            : LessEqualDepth;
          for (const edge of body.obj.edges.values()) {
            const { layer, visible } = edge.style;
            const c = renderNativeColors
              ? defaultEdgeColorComponents
              : layer
              ? //If the layer is a known vectorization layer, use the layer's color. Otherwise fallback to entity's style.color or host object's default color
                VECTORIZATION_EXPORT_LAYERS.find(({ key }) => key === layer)
                  ?.color ?? edge.defaultColor
              : // if no layer, then use entity's metaData.color or host's object's default color
              edge.metaData.color != null
              ? getOrCreateThreeColorArray(edge.metaData.color)
              : defaultEdgeColorComponents;
            const o = visible === false || visible === 0 ? 0 : 1;
            edge.setColorAndOpacity([...c, o], false);
          }

          //Paint texts
          for (const text of body.obj.texts.values()) {
            const { layer, visible } = text.style;
            const c = renderNativeColors
              ? defaultColor
              : layer
              ? //If the layer is a known vectorization layer, use the layer's color. Otherwise fallback to entity's style.color or host object's default color
                VECTORIZATION_EXPORT_LAYERS.find(({ key }) => key === layer)
                  ?.hexColor ?? text.defaultColor
              : // if no layer, then use entity's metaData.color or host's object's default color
                text.metaData.color ?? text.defaultColor;
            const o = visible === false || visible === 0 ? 0 : 1;
            text.setColor(c);
            text.opacity = o;
            text.depthFunc = onlyShowHighlighted ? AlwaysDepth : LessEqualDepth;
          }
        }

        //paint vertices (not visibile initially)
        for (const vertex of body.obj.vertices.values()) {
          vertex.renderable.visible = false;
        }

        //Because setColor and setOpacity calls above were with update === false
        body.obj.updateColors();
      }
    }

    for (const body of stockBodies) {
      // Skip bodies that aren't loaded yet
      if (!body.isLoaded) {
        continue;
      }
      //paint stockBodies' faces
      body.obj.facesGroup.visible = isBodyVisible(body);
      body.obj.faceMaterial.depthFunc = onlyShowHighlighted
        ? AlwaysDepth
        : LessEqualDepth;
      const isFocused =
        !allFocusedBodies || allFocusedBodies.has(body.id as string);
      for (const face of body.obj.faces.values()) {
        const c = renderNativeColors
          ? defaultColorArray
          : // if no layer, then use entity's metaData.color or host's object's default color
          face.metaData.color
          ? getOrCreateThreeColorArray(face.metaData.color)
          : face.defaultColor;
        const o = isFocused
          ? renderNativeColors
            ? MATERIAL_PROPERTIES.OPACITY
            : face.defaultOpacity
          : OPACITY_MAP.UNFOCUSED;
        face.setColorAndOpacity([...c, o], false);
      }
    }
  }
  // console.log(`paintAll(): ${performance.now() - start}`);
  return [isBodyVisible, stockBodies];
};

/*
For any faces not explicitly included in the nextState highlight, change their appearance.
Only works for faces of step objects
*/
export const handleUpdateHighlightView = (
  nextState: RendererState,
  [isBodyVisible, stockBodies] = createIsVisible(
    nextState.visibleStockResultKeys,
    nextState.bodyOrAssemblyInstanceById,
    nextState.visibleBodies,
  ),
): void => {
  if (nextState.onlyShowHighlighted && nextState.activeBodies.length) {
    const { bodies: nextBodies } = nextState;
    const faceIndices: Record<string, Set<number>> = {};
    for (const entry of chain(
      nextState.highlight || [],
      nextState.internalHighlight || [],
    )) {
      if (entry.type === ENTITY_TYPES.FACE) {
        const facesForBody = faceIndices[entry.body];
        if (facesForBody) {
          for (const ind of entry.indices) {
            facesForBody.add(ind);
          }
        } else {
          faceIndices[entry.body] = new Set(entry.indices);
        }
      }
    }
    for (const body of chain(nextBodies, stockBodies)) {
      if (
        isBodyVisible(body) &&
        body.isLoaded &&
        body.obj instanceof STEPBody
      ) {
        body.obj.facesGroup.visible = true;
        for (const face of body.obj.faces.values()) {
          if (faceIndices[body.id as string]?.has(face.index)) {
            continue;
          }
          const o =
            face.style.visible === false || face.style.visible === 0
              ? 0
              : OPACITY_MAP.HIGHLIGHT_ONLY_VIEW;
          face.setColorAndOpacity([...partColor, o]);
          face.depthFunc = nextState.onlyShowHighlighted
            ? AlwaysDepth
            : LessEqualDepth;
        }
      }
    }
  }
};

let visibleStock = new Set();

export const handleUpdatePaint = (
  thisState: RendererState,
  nextState: RendererState,
  force?: boolean,
): void => {
  // collect if the viewer needs to perform any changes to what is currently painted
  const requiresUpdate =
    force ||
    areObjectsDifferentForKeys(nextState, thisState, [
      'highlight',
      'internalHighlight',
      'activeBodies',
      'focusedBodies',
      'visibleBodies',
      'isFocusedViewActive',
      'selectedBodies',
      'wireframe',
      'renderNativeColors',
      'onlyShowHighlighted',
    ]);

  if (!requiresUpdate) return;

  // const start = performance.now();

  const {
    bodies: nextBodies,
    bodyOrAssemblyInstanceById,
    focusedBodies,
    isFocusedViewActive,
    onlyShowHighlighted,
    renderNativeColors,
    selectedBodies,
    visibleBodies,
    visibleStockResultKeys,
    wireframe,
  } = nextState;

  if (!force) {
    //eslint-disable-next-line no-param-reassign
    force =
      nextState.wireframe !== thisState.wireframe ||
      nextState.renderNativeColors !== thisState.renderNativeColors ||
      nextState.onlyShowHighlighted !== thisState.onlyShowHighlighted ||
      areArraysAsSetDifferent(nextState.visibleBodies, thisState.visibleBodies);
  }

  //Check if there is new visible stock piece and update known visibleStock, and force paintAll()
  if (visibleStockResultKeys?.length) {
    if (visibleStockResultKeys.some((k) => !visibleStock.has(k))) {
      visibleStock = new Set(visibleStockResultKeys);
      //eslint-disable-next-line no-param-reassign
      force = true;
    }
  } else {
    visibleStock.clear();
  }

  if (force) {
    clearCaches(); //cleared because we will be starting with paintAll();
  }

  const [isBodyVisible, stockBodies] = paintAll({
    bodies: nextBodies,
    bodyOrAssemblyInstanceById,
    onlyShowHighlighted,
    renderNativeColors,
    visibleBodies,
    visibleStockResultKeys,
    isFocusedViewActive,
    focusedBodies,
    wireframe,
    force,
  });

  // update selection color for any entities called for in selected bodies
  // only highlight selectdBodies faces if !wireframe
  if (!wireframe) {
    paintSelectedBodies({
      ids: selectedBodies,
      bodyOrAssemblyInstanceById,
      color: assemblySelectedColor,
      alwaysDepth: nextState.onlyShowHighlighted,
    });
  }

  paintAndUpdateHighlights(
    nextState.highlight,
    nextState.internalHighlight,
    wireframe,
    bodyOrAssemblyInstanceById,
    isBodyVisible,
  );

  handleUpdateHighlightView(nextState, [isBodyVisible, stockBodies]);

  for (const body of chain(nextBodies, stockBodies)) {
    // Skip bodies that aren't loaded yet
    if (!body.isLoaded) {
      continue;
    }
    if (body.obj instanceof STEPBody && body.obj.edgeLineSegments2?.length) {
      syncFlatPartLineSegments2Colors(body.obj);
    }
  }
};
